import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../App';

// Regional Directory Footer, programmatic-SEO crawl surface.
//
// Renders a clean text-link grid of every (city x service) combination
// grouped by UK region. Two purposes:
//   1. Helps human visitors discover the local landing pages.
//   2. Gives Googlebot a single page from which to crawl all 84 SEO
//      pages (sitemap is the canonical signal, but on-page links pass
//      PageRank in a way the sitemap alone cannot).
//
// Loads via /api/seo/directory/all so a future admin tool that
// reactivates / pauses cities reflects here without a code change.
// Hides itself on fetch failure (analytics > UX trade-off favours
// hiding rather than rendering a half-broken block).
export function RegionalDirectoryFooter() {
  const [regions, setRegions] = useState(null);

  useEffect(() => {
    api
      .get('/seo/directory/all')
      .then((res) => setRegions(res.data.regions || []))
      .catch(() => setRegions([]));
  }, []);

  if (!regions || regions.length === 0) return null;

  return (
    <section
      className="bg-white border-t border-stone-100 py-16"
      data-testid="regional-directory-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#1A1A1A] mb-2">
            Find stylists across the UK
          </h2>
          <p className="text-sm text-stone-500">
            Verified inclusive multi-cultural beauty professionals, city by city.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {regions.map((r) => (
            <div key={r.region} data-testid={`directory-region-${r.region.replace(/\s+/g, '-').toLowerCase()}`}>
              <h3 className="text-xs font-semibold text-[#D4AF37] tracking-[0.2em] uppercase mb-3">
                {r.region}
              </h3>
              <ul className="space-y-1.5">
                {r.cities.map((c) => (
                  <li key={c.slug}>
                    {/* City heading itself is not a link, the per-service
                        links below carry the SEO weight. Keeping it as a
                        plain h4 stops accidental clicks shadowing the
                        deeper links. */}
                    <p className="text-sm font-medium text-stone-700 mt-3 first:mt-0 mb-1.5">
                      {c.name}
                    </p>
                    <ul className="space-y-1">
                      {c.links.map((link) => (
                        <li key={link.url}>
                          <Link
                            to={link.url}
                            className="text-xs text-stone-500 hover:text-[#1A1A1A] hover:underline underline-offset-2"
                            data-testid={`directory-link-${c.slug}-${link.service_slug}`}
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
